"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { PrefsControls } from "./prefs-controls";
import { usePrefs } from "./prefs-provider";

type MeUser = { id: string; name: string; email: string } | null;

export function SiteHeader({ solid = false }: { solid?: boolean }) {
  const pathname = usePathname();
  const router = useRouter();
  const { t } = usePrefs();
  const [user, setUser] = useState<MeUser>(null);
  const [scrolled, setScrolled] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let alive = true;
    fetch("/api/auth/me")
      .then((r) => r.json())
      .then((d) => {
        if (alive) setUser(d.user ?? null);
      })
      .catch(() => {
        if (alive) setUser(null);
      })
      .finally(() => {
        if (alive) setReady(true);
      });
    return () => {
      alive = false;
    };
  }, [pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const opaque = solid || scrolled;

  async function logout() {
    await fetch("/api/auth/logout", { method: "POST" });
    setUser(null);
    router.push("/");
    router.refresh();
  }

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,box-shadow] duration-200 ${
        opaque
          ? "border-b border-[var(--line)] bg-[var(--bg)] shadow-[0_1px_0_rgba(11,31,26,0.04)]"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-3 px-4 sm:px-6">
        <Link href="/" className="font-display shrink-0 text-xl font-bold tracking-tight text-[var(--ink)]">
          Chati<span className="text-[var(--teal)]">Safe</span>
        </Link>
        <nav className="hidden items-center gap-8 text-sm font-medium text-[var(--ink-soft)] md:flex">
          <Link href="/#how" className="hover:text-[var(--ink)]">
            {t.nav.how}
          </Link>
          <Link href="/#protection" className="hover:text-[var(--ink)]">
            {t.nav.protection}
          </Link>
          <Link href="/plans" className="hover:text-[var(--ink)]">
            {t.nav.plans}
          </Link>
        </nav>
        <div className="flex items-center gap-2 sm:gap-3">
          <PrefsControls compact />
          {!ready ? (
            <div className="h-9 w-20 animate-pulse rounded-lg bg-black/5 dark:bg-white/10" />
          ) : user ? (
            <>
              <span className="hidden text-sm text-[var(--ink-soft)] lg:inline">
                {user.name.split(" ")[0]}
              </span>
              <Link href="/dashboard" className="btn-primary !px-3.5 !py-2 text-sm">
                {t.nav.dashboard}
              </Link>
              <button
                type="button"
                onClick={logout}
                className="hidden rounded-lg px-3 py-2 text-sm font-semibold text-[var(--ink-soft)] hover:text-[var(--ink)] sm:inline"
              >
                {t.nav.logout}
              </button>
            </>
          ) : (
            <>
              <Link
                href="/login"
                className="rounded-lg px-3 py-2 text-sm font-semibold text-[var(--ink-soft)] hover:text-[var(--ink)]"
              >
                {t.nav.login}
              </Link>
              <Link href="/register" className="btn-primary !px-3.5 !py-2 text-sm">
                {t.nav.startFree}
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

export function SiteFooter() {
  const { t } = usePrefs();
  const [user, setUser] = useState<MeUser>(null);

  useEffect(() => {
    fetch("/api/auth/me")
      .then((r) => r.json())
      .then((d) => setUser(d.user ?? null))
      .catch(() => setUser(null));
  }, []);

  return (
    <footer className="border-t border-[var(--line)] bg-[var(--bg-deep)]">
      <div className="mx-auto flex max-w-6xl flex-col gap-6 px-4 py-10 sm:flex-row sm:items-center sm:justify-between sm:px-6">
        <div>
          <p className="font-display text-lg font-bold">
            Chati<span className="text-[var(--teal)]">Safe</span>
          </p>
          <p className="mt-1 text-sm text-[var(--ink-soft)]">{t.footer.tagline}</p>
        </div>
        <div className="flex flex-wrap gap-5 text-sm text-[var(--ink-soft)]">
          <Link href="/plans">{t.nav.plans}</Link>
          <Link href="/privacy">{t.footer.privacy}</Link>
          <Link href="/terms">{t.footer.terms}</Link>
          {user ? <Link href="/dashboard">{t.nav.dashboard}</Link> : <Link href="/login">{t.nav.login}</Link>}
        </div>
      </div>
    </footer>
  );
}
